import {
  Directive,
  ElementRef,
  Renderer2,
  HostListener,
  Input,
  AfterViewInit
} from '@angular/core';

/**
 * Enhanced PaymentCardType Directive
 * - Single <img> approach to avoid multiple network calls
 * - Only update icon src if brand changes
 */
@Directive({
  selector: '[paymentCardType]',
  standalone: true
})
export class PaymentCardTypeDirective implements AfterViewInit {
  @Input() enableClass: boolean = false;
  @Input() enableIcon: boolean = false;
  @Input() iconContainerSelector: string | null = null;

  private iconContainerEl: HTMLElement | null = null;
  private iconImageEl: HTMLImageElement | null = null;
  private currentBrand: string = 'DEFAULT'; // track current brand to skip repeated updates

  private brandIcons: { [key: string]: string } = {
    AMEX: 'assets/amex.png',
    VISA: 'assets/visa.png',
    MASTERCARD: 'assets/mastercard.png',
    DISCOVER: 'assets/discover.png',
    DEFAULT: 'assets/generic-card.png'
  };

  private brandLabels: { [key: string]: string } = {
    AMEX: 'AMEX',
    VISA: 'VISA',
    MASTERCARD: 'MasterCard',
    DISCOVER: 'Discover',
    DEFAULT: 'Card'
  };

  private placeholders: { [key: string]: string } = {
    AMEX: '#### ###### #####',    // 4-6-5
    VISA: '#### #### #### ####',
    MASTERCARD: '#### #### #### ####',
    DISCOVER: '#### #### #### ####',
    DEFAULT: '#### #### #### ####'
  };

  private brandDigitLimits: { [key: string]: number } = {
    AMEX: 15,
    VISA: 16,
    MASTERCARD: 16,
    DISCOVER: 16,
    DEFAULT: 16
  };

  private brandStringLimits: { [key: string]: number } = {
    AMEX: 17,       // 15 digits + 2 spaces
    VISA: 19,       // 16 digits + 3 spaces
    MASTERCARD: 19,
    DISCOVER: 19,
    DEFAULT: 19
  };

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit() {
    // Locate the icon container (if requested)
    if (this.enableIcon && this.iconContainerSelector) {
      this.iconContainerEl = this.findIconContainer(
        this.el.nativeElement,
        this.iconContainerSelector
      );
      // Initialize icon to DEFAULT brand
      if (this.iconContainerEl) {
        this.updateIcon('DEFAULT');
      }
    }
  }

  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    // 1. Detect brand
    const cardType = this.detectCardType(value);

    // 2. Update placeholder
    this.updatePlaceholder(cardType);

    // 3. Apply classes (if enabled)
    if (this.enableClass) {
      this.updateCardTypeClass(cardType);
    }

    // 4. Update brand icon (only if brand changed)
    if (this.enableIcon && this.iconContainerEl) {
      if (cardType !== this.currentBrand) {
        this.updateIcon(cardType);
        this.currentBrand = cardType;
      }
    }

    // 5. Truncate extra digits
    const digitLimit = this.brandDigitLimits[cardType] || this.brandDigitLimits['DEFAULT'];
    const sanitized = value.replace(/\D/g, '').slice(0, digitLimit);

    // 6. Format
    const formatted = this.formatCardNumber(sanitized, cardType);
    this.renderer.setProperty(this.el.nativeElement, 'value', formatted);

    // Move caret to end (simplified approach)
    this.el.nativeElement.setSelectionRange(formatted.length, formatted.length);

    // 7. Set maxlength (with spaces)
    const maxStringLen = this.brandStringLimits[cardType] || this.brandStringLimits['DEFAULT'];
    this.renderer.setAttribute(this.el.nativeElement, 'maxlength', maxStringLen.toString());
  }

  /**
   * Determine brand from BIN patterns.
   */
  private detectCardType(value: string): string {
    const sanitized = (value || '').replace(/\D/g, '');

    // AMEX: 34 or 37
    if (/^3[47]\d{0,13}$/.test(sanitized)) {
      return 'AMEX';
    }
    // VISA: 4xxx
    if (/^4\d{0,15}$/.test(sanitized)) {
      return 'VISA';
    }
    // MasterCard: 51-55 or 2221-2720
    if (
      /^5[1-5]\d{0,14}$/.test(sanitized) ||
      /^2(2[2-9]|[3-6]\d|7[0-1])\d{0,12}$/.test(sanitized) ||
      /^2720\d{0,12}$/.test(sanitized)
    ) {
      return 'MASTERCARD';
    }
    // Discover: 6011, 65, 644-649, 622126-622925
    if (
      /^6011\d{0,12}$/.test(sanitized) ||
      /^65\d{0,14}$/.test(sanitized) ||
      /^64[4-9]\d{0,13}$/.test(sanitized) ||
      /^622(12[6-9]|1[3-9]\d|[2-8]\d{2}|9[0-1]\d|92[0-5])\d{0,10}$/.test(sanitized)
    ) {
      return 'DISCOVER';
    }

    return 'DEFAULT';
  }

  /**
   * Format a numeric string with spaces: AMEX (4-6-5), otherwise groups of 4.
   */
  private formatCardNumber(sanitized: string, cardType: string): string {
    if (cardType === 'AMEX') {
      const part1 = sanitized.substring(0, 4);
      const part2 = sanitized.substring(4, 10);
      const part3 = sanitized.substring(10, 15);

      let result = part1;
      if (part2) result += ' ' + part2;
      if (part3) result += ' ' + part3;

      return result.trim();
    } else {
      // Groups of 4 for Visa, MC, Discover, etc.
      const parts: string[] = [];
      for (let i = 0; i < sanitized.length; i += 4) {
        parts.push(sanitized.substring(i, i + 4));
      }
      return parts.join(' ').trim();
    }
  }

  private updatePlaceholder(cardType: string): void {
    const placeholder = this.placeholders[cardType] || this.placeholders['DEFAULT'];
    this.renderer.setAttribute(this.el.nativeElement, 'placeholder', placeholder);
  }

  private updateCardTypeClass(cardType: string): void {
    // Remove all brand classes
    Object.keys(this.placeholders).forEach(type => {
      this.renderer.removeClass(this.el.nativeElement, type.toLowerCase());
    });
    // Add new brand class
    this.renderer.addClass(this.el.nativeElement, cardType.toLowerCase());
  }

  /**
   * Update or create <img> only if brand changed.
   * If the <img> fails to load, fallback to text.
   */
  private updateIcon(cardType: string): void {
    const iconPath = this.brandIcons[cardType] || this.brandIcons['DEFAULT'];
    const brandLabel = this.brandLabels[cardType] || this.brandLabels['DEFAULT'];

    // If we haven't created an <img> yet, do so.
    if (!this.iconImageEl) {
      this.iconImageEl = this.renderer.createElement('img');
      if (!this.iconImageEl) return;
      this.renderer.setStyle(this.iconImageEl, 'height', '24px');
      this.renderer.setStyle(this.iconImageEl, 'margin-left', '8px');
      this.renderer.setStyle(this.iconImageEl, 'vertical-align', 'middle');

      // Attach error handler once
      this.iconImageEl.addEventListener('error', () => {
        // Remove the image node
        if (this.iconImageEl?.parentNode === this.iconContainerEl) {
          this.iconContainerEl?.removeChild(this.iconImageEl);
        }
        this.iconImageEl = null;

        // Fallback text node
        const textNode = this.renderer.createText(brandLabel);
        this.iconContainerEl?.appendChild(textNode);
      });

      // Append to container
      this.iconContainerEl?.appendChild(this.iconImageEl);
    } else {
      // If an image already exists, remove any fallback text from a previous load error
      this.clearTextFallback();
    }

    // Update <img> src & alt
    if (this.iconImageEl) {
      this.renderer.setAttribute(this.iconImageEl, 'src', iconPath);
      this.renderer.setAttribute(this.iconImageEl, 'alt', brandLabel);
    }
  }

  /**
   * Remove any text nodes if the image was replaced.
   */
  private clearTextFallback(): void {
    // If there's text fallback in container, remove it
    if (this.iconContainerEl) {
      // remove all text nodes
      Array.from(this.iconContainerEl.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          this.iconContainerEl?.removeChild(node);
        }
      });
    }
  }

  /**
   * Recursively find container from the input's parent up the DOM chain.
   */
  private findIconContainer(
    currentEl: HTMLElement,
    selector: string
  ): HTMLElement | null {
    if (!currentEl.parentElement) return null;
    const container = currentEl.parentElement.querySelector(selector) as HTMLElement;
    if (container) {
      return container;
    }
    return this.findIconContainer(currentEl.parentElement, selector);
  }
}
